import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

export const branchAllocationSourceTypeV1Schema = z.enum([
  "estimate",
  "visit",
  "invoice",
  "payment",
  "inventory",
  "payroll",
])

export const branchAllocationRecordV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.literal(0),
  allocationId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  configurationId: entityIdSchema,
  configurationVersion: z.number().int().positive(),
  sourceType: branchAllocationSourceTypeV1Schema,
  sourceId: entityIdSchema,
  sourceVersion: z.number().int().positive(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  sourceAmountMinor: z.number().int().positive(),
  method: z.enum(["direct", "measured", "approved_manual"]),
  methodVersion: z.number().int().positive(),
  reason: z.string().trim().min(1).max(1_000),
  lines: z.array(z.object({
    lineId: entityIdSchema,
    branchId: branchIdSchema,
    costCenter: z.string().trim().min(1).max(140),
    amountMinor: z.number().int().positive(),
  }).strict()).min(1).max(100),
}).strict().superRefine((value, context) => {
  const allocated = value.lines.reduce(
    (total, line) => total + line.amountMinor,
    0,
  )
  if (
    !Number.isSafeInteger(allocated) ||
    allocated !== value.sourceAmountMinor
  ) {
    context.addIssue({
      code: "custom",
      message: "Branch allocations must reproduce the source amount.",
    })
  }
})

export const branchAllocationResultV1Schema = z.object({
  entityType: z.literal("branch_allocation"),
  entityId: entityIdSchema,
  state: z.literal("recorded"),
  version: z.literal(1),
  legalEntityId: legalEntityIdSchema,
  sourceType: branchAllocationSourceTypeV1Schema,
  sourceId: entityIdSchema,
  sourceVersion: z.number().int().positive(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  sourceAmountMinor: z.number().int().positive(),
  allocatedAmountMinor: z.number().int().positive(),
  branchCount: z.number().int().positive(),
  legalDocumentSingleEntity: z.literal(true),
}).strict()

export type BranchAllocationRecordV1 = z.infer<
  typeof branchAllocationRecordV1Schema
>
